
import { karmaService } from './karmaService';
import { v4 as uuidv4 } from 'uuid';
import { createClient, SupabaseClient } from '@supabase/supabase-js';

export interface NPCProfile {
  id: string;
  name: string;
  type: 'npc';
  level: number;
  experience: number;
  karma: number;
  archetype: 'merchant' | 'guard' | 'sage' | 'wanderer' | 'faction_leader' | 'quest_giver' | 'companion';
  appearance: {
    description: string;
    imageUrl?: string;
  };
  stats: {
    health: { current: number; max: number };
    energy: { current: number; max: number };
    spiritualEnergy: { current: number; max: number };
    luck: number;
    emptiness: number;
  };
  professions: {
    primary: string;
    secondary?: string;
    skills: string[];
  };
  factionStandings: {
    [factionId: string]: {
      reputation: number;
      rank: string;
    };
  };
  playedCartridges: {
    cartridgeId: string;
    completionStatus: 'playing' | 'completed' | 'abandoned';
    lastPlayed: Date;
  }[];
  inventory: {
    itemId: string;
    quantity: number;
  }[];
  dialogue: {
    greetings: string[];
    farewells: string[];
    combatTaunts?: string[];
    tradeOffers?: string[];
    questHints?: string[];
  };
  behavior: {
    aggression: number; // 0-100
    friendliness: number; // 0-100
    tradingWillingness: number; // 0-100
    questGiving: boolean;
  };
  schedule?: {
    location: string;
    timeSlot: 'morning' | 'afternoon' | 'evening' | 'night';
  }[];
  relationships: {
    [entityId: string]: {
      relationship: number; // -100 to 100
      type: 'friend' | 'enemy' | 'neutral' | 'romantic' | 'family' | 'rival';
    };
  };
  lastInteraction?: Date;
  created_at: Date;
  updated_at: Date;
}

export interface NPCInteraction {
  npcId: string;
  playerId: string;
  type: 'dialogue' | 'trade' | 'combat' | 'quest' | 'social';
  outcome: 'positive' | 'negative' | 'neutral';
  karmaImpact: number;
  relationshipChange: number;
  timestamp: Date;
}

export interface NPCAchievement {
  npcId: string;
  type: 'level_up' | 'faction_promotion' | 'legendary_item' | 'quest_complete' | 'relationship_milestone' | 'karma_shift';
  title: string;
  description: string;
  karmaValue: number;
  imageUrl?: string;
  metadata: Record<string, any>;
}

class NPCProfileService {
  private npcCache: Map<string, NPCProfile> = new Map();
  private achievementHandlers: ((achievement: NPCAchievement) => void)[] = [];

  /**
   * Register an achievement handler for feed generation
   */
  onAchievement(handler: (achievement: NPCAchievement) => void) {
    this.achievementHandlers.push(handler);
  }

  /**
   * Create a new NPC profile
   */
  async createNPCProfile(
    data: Omit<NPCProfile, 'id' | 'created_at' | 'updated_at' | 'type'>
  ): Promise<NPCProfile> {
    const npcId = uuidv4();
    const now = new Date();
    
    const npcProfile: NPCProfile = {
      ...data,
      id: npcId,
      type: 'npc',
      created_at: now,
      updated_at: now
    };

    // Store in Supabase
    const { error } = await this.getSupabase()
      .from('npc_profiles')
      .insert({
        id: npcId,
        profile_data: npcProfile,
        name: data.name,
        level: data.level,
        karma: data.karma,
        archetype: data.archetype
      });

    if (error) {
      console.error('[NPCProfileService] Error creating NPC profile:', error);
      throw new Error('Failed to create NPC profile');
    }

    this.npcCache.set(npcId, npcProfile);
    return npcProfile;
  }

  /**
   * Get NPC profile by ID
   */
  async getNPCProfile(npcId: string): Promise<NPCProfile | null> {
    // Check cache first
    if (this.npcCache.has(npcId)) {
      return this.npcCache.get(npcId)!;
    }

    const { data, error } = await this.getSupabase()
      .from('npc_profiles')
      .select('*')
      .eq('id', npcId)
      .single();

    if (error || !data) {
      console.error('[NPCProfileService] Error fetching NPC profile:', error);
      return null;
    }

    const profile = data.profile_data as NPCProfile;
    this.npcCache.set(npcId, profile);
    return profile;
  }

  /**
   * Update NPC stats and check for achievements
   */
  async updateNPCStats(
    npcId: string,
    updates: Partial<NPCProfile['stats']>
  ): Promise<void> {
    const npc = await this.getNPCProfile(npcId);
    if (!npc) return;

    const oldStats = { ...npc.stats };
    npc.stats = { ...npc.stats, ...updates };
    
    await this.updateNPCProfile(npcId, { stats: npc.stats });

    // Check for level ups or other stat-based achievements
    if (updates.health && oldStats.health.max < npc.stats.health.max) {
      this.triggerAchievement({
        npcId,
        type: 'level_up',
        title: `${npc.name} Grows Stronger`,
        description: `Health increased from ${oldStats.health.max} to ${npc.stats.health.max}`,
        karmaValue: 10,
        metadata: { oldHealth: oldStats.health.max, newHealth: npc.stats.health.max }
      });
    }
  }

  /**
   * Add experience to NPC and handle level ups
   */
  async addExperience(npcId: string, amount: number): Promise<void> {
    const npc = await this.getNPCProfile(npcId);
    if (!npc) return;

    const oldLevel = npc.level;
    const oldExp = npc.experience;
    npc.experience += amount;

    // Simple level up formula: 100 * level^1.5
    const expNeeded = Math.floor(100 * Math.pow(npc.level, 1.5));
    
    if (npc.experience >= expNeeded) {
      npc.level++;
      npc.experience -= expNeeded;
      
      // Increase stats on level up
      npc.stats.health.max += 10;
      npc.stats.health.current = npc.stats.health.max;
      npc.stats.energy.max += 5;
      npc.stats.energy.current = npc.stats.energy.max;
      
      // Trigger level up achievement
      this.triggerAchievement({
        npcId,
        type: 'level_up',
        title: `${npc.name} Reaches Level ${npc.level}!`,
        description: `Advanced from level ${oldLevel} after gaining ${amount} experience`,
        karmaValue: 20,
        metadata: {
          oldLevel,
          newLevel: npc.level,
          experienceGained: amount
        }
      });
    }

    await this.updateNPCProfile(npcId, {
      level: npc.level,
      experience: npc.experience,
      stats: npc.stats
    });
  }

  /**
   * Update faction standing and check for promotions
   */
  async updateFactionStanding(
    npcId: string,
    factionId: string,
    reputationChange: number
  ): Promise<void> {
    const npc = await this.getNPCProfile(npcId);
    if (!npc) return;

    const oldStanding = npc.factionStandings[factionId] || { reputation: 0, rank: 'Neutral' };
    const newReputation = oldStanding.reputation + reputationChange;
    
    // Determine new rank based on reputation
    let newRank = 'Neutral';
    if (newReputation >= 1000) newRank = 'Exalted';
    else if (newReputation >= 500) newRank = 'Revered';
    else if (newReputation >= 200) newRank = 'Honored';
    else if (newReputation >= 50) newRank = 'Friendly';
    else if (newReputation < -500) newRank = 'Hated';
    else if (newReputation < -200) newRank = 'Hostile';
    else if (newReputation < -50) newRank = 'Unfriendly';

    npc.factionStandings[factionId] = {
      reputation: newReputation,
      rank: newRank
    };

    // Check for rank change achievement
    if (oldStanding.rank !== newRank) {
      this.triggerAchievement({
        npcId,
        type: 'faction_promotion',
        title: `${npc.name}'s Faction Standing Changed`,
        description: `Now ${newRank} with faction (${newReputation} reputation)`,
        karmaValue: Math.abs(reputationChange) > 100 ? 30 : 15,
        metadata: {
          factionId,
          oldRank: oldStanding.rank,
          newRank,
          reputationChange
        }
      });
    }

    await this.updateNPCProfile(npcId, { factionStandings: npc.factionStandings });
  }

  /**
   * Record NPC playing a cartridge
   */
  async recordCartridgePlay(
    npcId: string,
    cartridgeId: string,
    status: 'playing' | 'completed' | 'abandoned' = 'playing'
  ): Promise<void> {
    const npc = await this.getNPCProfile(npcId);
    if (!npc) return;

    const existingIndex = npc.playedCartridges.findIndex(pc => pc.cartridgeId === cartridgeId);
    
    if (existingIndex >= 0) {
      npc.playedCartridges[existingIndex].completionStatus = status;
      npc.playedCartridges[existingIndex].lastPlayed = new Date();
    } else {
      npc.playedCartridges.push({
        cartridgeId,
        completionStatus: status,
        lastPlayed: new Date()
      });
    }

    if (status === 'completed') {
      this.triggerAchievement({
        npcId,
        type: 'quest_complete',
        title: `${npc.name} Completed a Story`,
        description: `Finished their journey through a dream cartridge`,
        karmaValue: 50,
        metadata: { cartridgeId }
      });
    }

    await this.updateNPCProfile(npcId, { playedCartridges: npc.playedCartridges });
  }

  /**
   * Update relationship between NPC and another entity
   */
  async updateRelationship(
    npcId: string,
    entityId: string,
    relationshipChange: number,
    type?: 'friend' | 'enemy' | 'neutral' | 'romantic' | 'family' | 'rival'
  ): Promise<void> {
    const npc = await this.getNPCProfile(npcId);
    if (!npc) return;

    const oldRelationship = npc.relationships[entityId]?.relationship || 0;
    const newRelationship = Math.max(-100, Math.min(100, oldRelationship + relationshipChange));
    
    // Determine relationship type based on value if not specified
    if (!type) {
      if (newRelationship >= 80) type = 'friend';
      else if (newRelationship >= 50) type = 'friend';
      else if (newRelationship <= -80) type = 'enemy';
      else if (newRelationship <= -50) type = 'rival';
      else type = 'neutral';
    }

    npc.relationships[entityId] = {
      relationship: newRelationship,
      type
    };

    // Check for relationship milestones
    const crossedMilestone = 
      (oldRelationship < 50 && newRelationship >= 50) || // Became friends
      (oldRelationship < 80 && newRelationship >= 80) || // Became close friends
      (oldRelationship > -50 && newRelationship <= -50) || // Became rivals
      (oldRelationship > -80 && newRelationship <= -80); // Became enemies

    if (crossedMilestone) {
      this.triggerAchievement({
        npcId,
        type: 'relationship_milestone',
        title: `${npc.name}'s Relationship Changed`,
        description: `Relationship status: ${type} (${newRelationship})`,
        karmaValue: 25,
        metadata: {
          entityId,
          oldRelationship,
          newRelationship,
          type
        }
      });
    }

    await this.updateNPCProfile(npcId, { relationships: npc.relationships });
  }

  /**
   * Handle NPC acquiring items
   */
  async addItemToInventory(
    npcId: string,
    itemId: string,
    quantity: number = 1,
    itemData?: { name: string; rarity: number }
  ): Promise<void> {
    const npc = await this.getNPCProfile(npcId);
    if (!npc) return;

    const existingItem = npc.inventory.find(item => item.itemId === itemId);
    
    if (existingItem) {
      existingItem.quantity += quantity;
    } else {
      npc.inventory.push({ itemId, quantity });
    }

    // Check for legendary item achievement
    if (itemData && itemData.rarity >= 5) {
      this.triggerAchievement({
        npcId,
        type: 'legendary_item',
        title: `${npc.name} Acquired Legendary Item`,
        description: `Obtained ${itemData.name}`,
        karmaValue: 40,
        metadata: { itemId, itemName: itemData.name, rarity: itemData.rarity }
      });
    }

    await this.updateNPCProfile(npcId, { inventory: npc.inventory });
  }

  /**
   * Process karma changes and check for alignment shifts
   */
  async updateKarma(npcId: string, karmaChange: number, reason: string): Promise<void> {
    const npc = await this.getNPCProfile(npcId);
    if (!npc) return;

    const oldKarma = npc.karma;
    const oldAlignment = karmaService.getKarmaAlignment(oldKarma);
    
    npc.karma += karmaChange;
    const newAlignment = karmaService.getKarmaAlignment(npc.karma);

    // Check for alignment shift
    if (oldAlignment !== newAlignment) {
      this.triggerAchievement({
        npcId,
        type: 'karma_shift',
        title: `${npc.name}'s Karma Shifted`,
        description: `Alignment changed from ${oldAlignment} to ${newAlignment}`,
        karmaValue: Math.abs(karmaChange),
        metadata: {
          oldKarma,
          newKarma: npc.karma,
          oldAlignment,
          newAlignment,
          reason
        }
      });
    }

    await this.updateNPCProfile(npcId, { karma: npc.karma });

    // Apply karma ripple effects
    await karmaService.applyKarmaRipples('npc_action', npcId, karmaChange, [npcId], 3);
  }

  /**
   * Update NPC profile
   */
  private async updateNPCProfile(
    npcId: string,
    updates: Partial<NPCProfile>
  ): Promise<void> {
    const npc = await this.getNPCProfile(npcId);
    if (!npc) return;

    Object.assign(npc, updates);
    npc.updated_at = new Date();

    const { error } = await this.getSupabase()
      .from('npc_profiles')
      .update({
        profile_data: npc,
        name: npc.name,
        level: npc.level,
        karma: npc.karma,
        updated_at: npc.updated_at
      })
      .eq('id', npcId);

    if (error) {
      console.error('[NPCProfileService] Error updating NPC profile:', error);
      throw new Error('Failed to update NPC profile');
    }

    this.npcCache.set(npcId, npc);
  }

  /**
   * Trigger achievement and notify handlers
   */
  private triggerAchievement(achievement: NPCAchievement): void {
    console.log('[NPCProfileService] NPC Achievement:', achievement);
    
    // Notify all registered handlers (like feed service)
    this.achievementHandlers.forEach(handler => {
      try {
        handler(achievement);
      } catch (error) {
        console.error('[NPCProfileService] Achievement handler error:', error);
      }
    });
  }

  /**
   * Get NPCs by location for rendering
   */
  async getNPCsByLocation(location: string): Promise<NPCProfile[]> {
        const { data, error } = await this.getSupabase()
            .from('npc_locations')
            .select('npc_profiles (*)')
            .eq('location', location);

        if (error) {
            console.error('[NPCProfileService] Error fetching NPCs by location:', error);
            return [];
        }

        return (data || []).map(row => (row.npc_profiles as any).profile_data as NPCProfile);
    }

  /**
   * Generate NPC dialogue based on context
   */
  async generateContextualDialogue(
    npcId: string,
    playerId: string,
    context: 'greeting' | 'farewell' | 'trade' | 'quest' | 'combat'
  ): Promise<string> {
    const npc = await this.getNPCProfile(npcId);
    if (!npc) return "...";

    const relationship = npc.relationships[playerId]?.relationship || 0;
    
    // Select dialogue based on relationship and context
    let dialoguePool: string[] = [];
    
    switch (context) {
      case 'greeting':
        dialoguePool = npc.dialogue.greetings;
        break;
      case 'farewell':
        dialoguePool = npc.dialogue.farewells;
        break;
      case 'trade':
        dialoguePool = npc.dialogue.tradeOffers || [];
        break;
      case 'quest':
        dialoguePool = npc.dialogue.questHints || [];
        break;
      case 'combat':
        dialoguePool = npc.dialogue.combatTaunts || [];
        break;
    }

    // Modify dialogue based on relationship
    if (dialoguePool.length > 0) {
      const baseDialogue = dialoguePool[Math.floor(Math.random() * dialoguePool.length)];
      
      if (relationship >= 50) {
        return `${baseDialogue} Friend.`;
      } else if (relationship <= -50) {
        return `${baseDialogue} ...if I must.`;
      }
      
      return baseDialogue;
    }

    return "...";
  }
}

export const npcProfileService = new NPCProfileService();